import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import { getMovieById, getRelated } from "@/utils/movieUtils";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => getMovieById(id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: movie
      ? `${movie.title}剧情介绍与在线观看 - 岳母的诱惑`
      : "影视详情 - 岳母的诱惑",
    description: movie
      ? `${movie.title}：${movie.region}${movie.type}，${movie.genres.join("、")}类型。${movie.synopsis.slice(0, 60)}…`
      : "查看影视作品详情与剧情介绍。",
    keywords: movie
      ? `岳母的诱惑,${movie.title},${movie.type},${movie.genres.join(",")},剧情介绍`
      : "岳母的诱惑,影视详情",
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2000);
  };

  if (!movie) {
    return (
      <Layout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20">
          <EmptyState
            icon="ri-file-unknow-line"
            title="该影视内容已下线或不存在"
            description="你访问的作品可能已被移除，或链接有误。可以返回首页继续浏览其他影视内容。"
            action={
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            }
          />
        </div>
      </Layout>
    );
  }

  const related = getRelated(movie, 5);
  const favorited = isFavorite(movie.id);

  const handleFavorite = () => {
    toggleFavorite(movie.id);
    showToast(favorited ? "已取消收藏" : "已加入收藏");
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        showToast("链接已复制到剪贴板");
      } else {
        showToast(`作品链接：${url}`);
      }
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const metaRows = [
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "年份", value: `${movie.year} 年` },
    { label: "更新时间", value: movie.updateTime },
    { label: "导演", value: `${movie.director}（虚构）` },
    { label: "主演", value: `${movie.cast.join("、")}（虚构）` },
  ];

  return (
    <Layout overlayNav>
      <section className="relative w-full">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/85 to-background-950/60" />
        </div>

        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-10 md:pb-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "分类", to: "/category" },
              { label: movie.type, to: `/category?type=${encodeURIComponent(movie.type)}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="w-40 sm:w-48 md:w-56 shrink-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-700 bg-background-800">
                <img
                  src={movie.cover}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 剧情介绍`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-600 text-background-50">
                  {movie.type}
                </span>
                <span className="px-2.5 py-1 rounded-md text-xs bg-background-900/80 text-accent-400 border border-background-700">
                  <i className="ri-star-fill mr-1"></i>
                  {movie.rating}
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-50 leading-tight">
                {movie.title}
              </h1>
              <p className="mt-3 text-sm md:text-base text-foreground-400">
                {movie.genres.join(" · ")} · {movie.region} · {movie.year} · 热度{" "}
                {(movie.heat / 1000).toFixed(1)}k
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?genre=${encodeURIComponent(
                      movie.genres[0]
                    )}&type=${encodeURIComponent(movie.type)}`}
                    className="px-3 py-1 rounded-md text-xs bg-background-900 border border-background-800 text-foreground-300 hover:border-accent-500 hover:text-accent-400 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    favorited
                      ? "bg-accent-500 text-foreground-950"
                      : "bg-primary-600 hover:bg-primary-500 text-background-50"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-900/80 backdrop-blur border border-background-700 hover:border-accent-500 text-foreground-100 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>

              <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-background-900/70 border border-background-800 text-xs text-foreground-400">
                <i className="ri-information-line text-accent-400"></i>
                本站目前提供剧情介绍与信息浏览，暂不提供视频播放与下载功能。
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <article className="lg:col-span-2">
            <div className="p-5 md:p-6 rounded-lg bg-background-900/60 border border-background-800">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-50 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-300 leading-loose text-justify">
                {movie.synopsis}
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-400 leading-loose text-justify">
                以上剧情为本站整理的虚构示例内容，用于展示作品信息结构。实际的剧情走向与人物设定，请以官方发布的正式信息为准。若你对该类型作品感兴趣，可以通过页面下方的分类标签，浏览更多风格相近的影视内容。
              </p>
            </div>

            <div className="mt-6 p-5 md:p-6 rounded-lg bg-background-900/60 border border-background-800">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-50 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                作品信息
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="text-foreground-500 shrink-0 w-16">{row.label}</dt>
                    <dd className="text-foreground-200">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="p-5 rounded-lg bg-background-900/60 border border-background-800 lg:sticky lg:top-24">
              <h2 className="font-heading text-base font-bold text-foreground-100 mb-4">
                所属分类
              </h2>
              <div className="flex flex-wrap gap-2 mb-6">
                <Link
                  to={`/category?type=${encodeURIComponent(movie.type)}`}
                  className="px-3 py-1.5 rounded-md text-xs bg-secondary-900/60 border border-secondary-700 text-secondary-100 hover:border-accent-500 hover:text-accent-400 transition-colors"
                >
                  {movie.type}分类
                </Link>
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}&type=${encodeURIComponent(
                      movie.type
                    )}`}
                    className="px-3 py-1.5 rounded-md text-xs bg-background-900 border border-background-800 text-foreground-300 hover:border-accent-500 hover:text-accent-400 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
                <Link
                  to={`/category?region=${encodeURIComponent(movie.region)}&type=${encodeURIComponent(
                    movie.type
                  )}`}
                  className="px-3 py-1.5 rounded-md text-xs bg-background-900 border border-background-800 text-foreground-300 hover:border-accent-500 hover:text-accent-400 transition-colors"
                >
                  {movie.region}
                </Link>
              </div>

              <h2 className="font-heading text-base font-bold text-foreground-100 mb-4">
                相关推荐
              </h2>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex items-center gap-3"
                  >
                    <div className="w-12 h-16 rounded-md overflow-hidden bg-background-800 shrink-0">
                      <img
                        src={item.cover}
                        alt={`${item.title} 海报`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-200 truncate group-hover:text-accent-400 transition-colors">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-500 truncate">
                        {item.genres.join(" · ")}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-50">
              你可能也喜欢
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {related.map((item, index) => (
              <MovieCard key={item.id} movie={item} index={index} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium animate-fade-in-up">
          {toast}
        </div>
      )}
    </Layout>
  );
}